/**
 * @Author: Jon Lucas
 * @Date:   2017-09-15T17:27:26+02:00
 * @Email:  jon.lucas@mazedia.fr
 * @Last modified by:   Jon Lucas
 * @Last modified time: 2017-09-29T12:00:58+02:00
 */

import { Component, AfterViewInit, Input, SimpleChanges, EventEmitter, Output, OnDestroy, OnChanges, ElementRef } from "@angular/core";
import { ConfDataType } from "../../../conf-data-type";
import { CamConfType } from "./cam-conf-type";
import { BaseDataType } from "../../../base-data-type";
import { SmoothShading } from "three";
import { LoaderUtils } from "../../../util/LoaderUtils";
import { updateFor } from "typescript";

// import * as THREE from 'three'

declare var THREE: any;

// import "imports?THREE=three!controls/OrbitControls";
// import "imports?THREE=three!loaders/OBJLoader2";
// import "imports?THREE=three!loaders/MTLLoader";

// declare var OBJLoader2:any;
// declare var MTLLoader:any;
// declare var OrbitControls:any;

/* Allow the use of require in the code  */
// declare var require : any;
//
// var MTLLoader = require('three-mtl-loader');
// var OBJLoader = require('three-obj-loader');

@Component({
    selector: "app-threed-view",
    templateUrl: "./threed-view.component.html",
    styleUrls: ["./threed-view.component.scss"]
})
export class ThreedViewComponent implements AfterViewInit, OnDestroy, OnChanges {
    
    public readonly TAG: string = "[ThreedViewComponent]";
    
    private canvas: HTMLCanvasElement;
    
    @Input() public "base-model-path": string;
    
    @Output() public onModelLoaded = new EventEmitter<string>();
    
    @Input() public cameraDefaults: CamConfType;
    
    private _projectType: string;
    
    public scene: THREE.Scene;
    public renderer: THREE.WebGLRenderer;
    public camera: THREE.PerspectiveCamera;
    private controls: THREE.OrbitControls;
    private pivot: THREE.Object3D;
    private cameraTarget: THREE.Vector3;
    private aspectRatio: number;
    private flatShading: boolean;
    private doubleSide: boolean;
    private feedbackArray: string[];
    
    private textureLoader: THREE.TextureLoader;
    private materials: { [key: string]: THREE.MeshBasicMaterial[]; };
    
    private _objLoaded: { [key: string]: THREE.Object3D; } = {};
    private _objLoadedById: { [key: string]: THREE.Object3D; } = {};
    private _currentSceneObjs: { [key: string]: string; } = {};
    private _baseModel: THREE.Object3D;
    
    private _viewWidth: number;
    private _viewHeight: number;
    
    private _maxDim: number;

    private _tiltMinRad: number;
    private _tiltMaxRad: number;

    private readonly fps: number = 30;
    private readonly interval: number = 1000 / this.fps;
    private now: number;
    private then: number = Date.now();
    private delta: number;
    
    private _onResizeEventHandlerBinded: (e: Event) => void;

    constructor(private el: ElementRef) { }

    private init(): void {
        this.textureLoader = new THREE.TextureLoader();

        this.renderer = null;
        this.aspectRatio = 1;
        this.recalcAspectRatio();
        this.scene = null;

        // this.cameraDefaults = {
        //     posCamera: new THREE.Vector3(10.0, 5.0, 10.0),
        //     posCameraTarget: new THREE.Vector3(0, 0, 0),
        //     near: 1,
        //     far: 10000,
        //     fov: 35
        // };

        this.camera = null;
        this.cameraTarget = new THREE.Vector3();
        this.cameraTarget.copy(this.cameraDefaults.posCameraTarget);
        this.controls = null;
        this.flatShading = false;
        this.doubleSide = false;
        this.pivot = null;
        this.feedbackArray = [];

        this.initScene();
    }

    private initScene(): void {
        this.renderer = new THREE.WebGLRenderer({
            canvas: this.canvas,
            antialias: true
        });
        // this.renderer.shadowMapEnabled = true;
        // to antialias the shadow
        // this.renderer.shadowMapType = THREE.PCFSoftShadowMap;

        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(0xEFEFEF);
        this.camera = new THREE.PerspectiveCamera(
            this.cameraDefaults.fov,
            this.aspectRatio,
            this.cameraDefaults.near,
            this.cameraDefaults.far
        );

        this.resetCamera();

        this._tiltMinRad = THREE.Math.degToRad(this.cameraDefaults.tilt.min);
        this._tiltMaxRad = THREE.Math.degToRad(this.cameraDefaults.tilt.max);

        this.controls = new THREE.OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enablePan = false;
        this.controls.minDistance = this.cameraDefaults.distance.min;
        this.controls.maxDistance = this.cameraDefaults.distance.max;
        this.controls.minPolarAngle = this._tiltMinRad;
        this.controls.maxPolarAngle = this._tiltMaxRad;
        this.controls.minAzimuthAngle = THREE.Math.degToRad(this.cameraDefaults.pan.min);
        this.controls.maxAzimuthAngle = THREE.Math.degToRad(this.cameraDefaults.pan.max);
        this.controls.target.set(this.cameraTarget.x, this.cameraTarget.y, this.cameraTarget.z);


        const ambientLight = new THREE.AmbientLight(0xEFEFEF, 0.6);
        const directionalLight1 = new THREE.DirectionalLight(0xEFEFFF, 0.3); // C0C090);
        const directionalLight2 = new THREE.DirectionalLight(0xEFEFEF, 0.5); // C0C090);
        // directionalLight1.position.set(-5, 10, 10);
        // directionalLight2.position.set(10, -5, 10);
        directionalLight1.position.set(5, 5, 10);
        directionalLight2.position.set(-50, -100, 5);

        // directionalLight1.castShadow = true;
        // directionalLight1.shadow = new THREE.LightShadow( new THREE.OrthographicCamera( this._viewWidth / -2, this._viewWidth / 2, this._viewHeight / 2, this._viewHeight / -2, 1, 1000 ) );
        // directionalLight1.shadow.bias = 0.0001;
        // directionalLight1.shadow.mapSize.width = 2048;
        // directionalLight1.shadow.mapSize.height = 2048;

        // directionalLight2.castShadow = true;
        // directionalLight2.shadow = new THREE.LightShadow( new THREE.OrthographicCamera( this._viewWidth / -2, this._viewWidth / 2, this._viewHeight / 2, this._viewHeight / -2, 0.5, 1000 ) );
        // console.log(this.TAG, "SHADOW", directionalLight2.shadow);
        // directionalLight2.shadow.mapSize.width = 2048;
        // directionalLight2.shadow.mapSize.height = 2048;

        this.scene.add(directionalLight1);
        this.scene.add(directionalLight2);
        this.scene.add(ambientLight);

        // let helper = new THREE.DirectionalLightHelper( directionalLight1, 5 );
        // this.scene.add(helper);
        // helper = new THREE.DirectionalLightHelper( directionalLight2, 5 );
        // this.scene.add(helper);

        // let helper = new THREE.GridHelper( 1200, 60, 0xFF4444, 0x404040 );
        // this.scene.add( helper );
        this.pivot = new THREE.Object3D();
        this.pivot.name = "Pivot";
        this.scene.add(this.pivot);

        this.render();

        this.initBindedEvents();

        this.addListeners();
    }

    protected initBindedEvents(): void {
        this._onResizeEventHandlerBinded = this._onResizeEventHandler.bind(this);
    }

    public loadObj(objDef: any): void {

        LoaderUtils.showLoader("threed-loader");

        // this._reportProgress( 'Loading: ' + objDef.fileObj, objDef.id );
        const mtlLoader = new THREE.MTLLoader();
        mtlLoader.setPath(objDef.texturePath || "");
        (<any>mtlLoader).setCrossOrigin("anonymous");
        mtlLoader.load(objDef.fileMtl, (materials) => {
            materials.preload();
            this.pivot.add(objDef.pivot);
            const objLoader = new THREE.OBJLoader2();
            objLoader.setSceneGraphBaseNode(objDef.pivot);
            objLoader.setMaterials(materials.materials);
            objLoader.setPath(objDef.path);
            objLoader.setDebug(false, false);
            const onSuccess = (object3d: THREE.Object3D) => {
                console.log(this.TAG, objDef.fileObj, "Loading complete. Meshes were attached to: " + object3d.name);

                // If current object loaded, applies tint.
                // If not current object, removes it.
                // Like this, we can change selected model without waiting previous one is loaded.
                if (this._currentSceneObjs[objDef.id] === objDef.path + objDef.fileObj) {   // Update tint
                    if (objDef.tint) {
                        this._updateColor(objDef.id, objDef.tint, false);
                    }
                }
                else {
                    this.pivot.remove(objDef.pivot);
                }

                this.parseMaterials(objDef.id, materials.materials);

                this._objLoaded[objDef.path + objDef.fileObj] = objDef.pivot;
                this._objLoadedById[objDef.id] = objDef.pivot;

                if (objDef.id === "base-model") {
                    this._defineBaseModelMaxDimension(object3d);
                    this._baseModel = object3d;
                    // this._baseModel.receiveShadow = true;
                    // this._baseModel.castShadow = true;
                    if (this._projectType === undefined)
                    {
                        this.setLargeView(false);
                    } else {
                        this.setNormalView(false);
                    }
                }

                // object3d.receiveShadow = true;
                // object3d.castShadow = true;

                this.showConfiguration(this._projectType);

                // Dispatch event listenable by parent component:
                // https://angular.io/guide/component-interaction#parent-listens-for-child-event
                this.onModelLoaded.emit(objDef.id);

                LoaderUtils.hideLoader("threed-loader");

                // console.log( this.TAG, "OBJ LOADED", objDef.id );
            };
            const onProgress = function (event) {
                if (event.lengthComputable) {
                    const percentComplete = event.loaded / event.total * 100;
                    const output = "Download of \"" + objDef.fileObj + "\": " + Math.round(percentComplete) + "%";
                    // console.log(output);
                    // this._reportProgress( output, objDef.id );
                }
            };
            const onError = function (event) {
                const output = "Error of type \"" + event.type + "\" occurred when trying to load: " + event.src;
                // console.log(output);
                // this._reportProgress( output, objDef.id );
            };
            objLoader.load(objDef.fileObj, onSuccess, onProgress, onError);
        });
    }

    @Input() public set projectType(pType: string) {
        console.log(this.TAG, "projectType", pType);

        if (this._projectType === pType)    { return; }

        this._projectType = pType;

        if (this._projectType === undefined) {
            this.setLargeView(false);
        } else {
            this.setNormalView();
        }

        this.showConfiguration(this._projectType);
    }

    public setLargeView(pTween: boolean = true): void
    {
        if (!this.controls) { return; }

        if (this._projectType === undefined && this.controls.minDistance < this.cameraDefaults.distance.large)
        {
            this.controls.maxPolarAngle = THREE.Math.degToRad(this.cameraDefaults.tilt.large);

            if (pTween)
            {
                if (this.controls.minDistance !== this.cameraDefaults.distance.large)  {
                    requestAnimationFrame(this.setLargeView.bind(this));

                    this.now = Date.now();
                    this.delta = this.now - this.then;

                    if (this.delta > this.interval)
                    {
                        this.controls.minDistance = Math.min(this.cameraDefaults.distance.large, this.controls.minDistance + 1);
                        this.then = this.now - (this.delta % this.interval);
                    }
                }
                else
                {
                   this.controls.minDistance = this.cameraDefaults.distance.min;
                   this.controls.maxDistance = this.cameraDefaults.distance.large;
                }
            }
            else
            {
                this.controls.minDistance = this.cameraDefaults.distance.large;
                this.controls.maxDistance = this.cameraDefaults.distance.large;
                this.controls.update();
                this.controls.minDistance = this.cameraDefaults.distance.min;
            }
        }
    }

    public setNormalView(pTween: boolean = true): void
    {
        if (!this.controls) { return; }

        if (this._projectType !== undefined && this.controls.maxDistance > this.cameraDefaults.distance.max)
        {
            this.controls.maxPolarAngle = this._tiltMaxRad;

            if (pTween)
            {
                if (this.controls.maxDistance !== this.cameraDefaults.distance.max)  {
                    requestAnimationFrame(this.setNormalView.bind(this));
                    
                    this.now = Date.now();
                    this.delta = this.now - this.then;
                    
                    if (this.delta > this.interval)
                    {
                        this.controls.maxDistance = Math.max(this.cameraDefaults.distance.max, this.controls.maxDistance - 1);
                        this.then = this.now - (this.delta % this.interval);
                    }
                }
                else
                {
                    this.controls.maxDistance = this.cameraDefaults.distance.max;
                    this.controls.minDistance = this.cameraDefaults.distance.min;
                }
            }
            else
            {
                this.controls.minDistance = this.cameraDefaults.distance.max;
                this.controls.maxDistance = this.cameraDefaults.distance.max;
                this.controls.update();
                this.controls.minDistance = this.cameraDefaults.distance.min;
            }
        }
    }

    public setBelrivView(): void
    {
        if (!this.controls) { return; }

        if (this.controls.minPolarAngle !== this._tiltMaxRad)
        {
            requestAnimationFrame(this.setBelrivView.bind(this));

            this.now = Date.now();
            this.delta = this.now - this.then;

            if (this.delta > this.interval)
            {
                this.controls.minPolarAngle = Math.min(this._tiltMaxRad, Math.ceil((this.controls.minPolarAngle + (this._tiltMaxRad - this.controls.minPolarAngle) * 0.1) * 100) / 100);
                this.then = this.now - (this.delta % this.interval);
            }
        }
        else
        {
            this.controls.minPolarAngle = this._tiltMinRad;
            this.controls.maxPolarAngle = this._tiltMaxRad;
        }
    }

    /**
     * Show/hide base model's current projet type's geometry.
     *
     * @param pType {string}
     */
    public showConfiguration(pType: string): void {
        if (!this.pivot) { return; }

        // Search string portion present in
        // all visibility switchable meshes.
        let searchString: string = "_1";
        let isBelrivProject: boolean;
        switch (pType) {
            case "projet-debord":
            case "projet-goutiere-debord":
                isBelrivProject = true;
                // searchString = "_1";
                break;

            default:
                isBelrivProject = false;
                // searchString = "_2";
                break;
        }

        let mesh: THREE.Mesh;
        for (const child of this.pivot.children) {
            if (child instanceof THREE.Object3D) {
                // console.log(this.TAG, "SCENE CHILDREN", child);
                for (const m of child.children) {
                    if (m instanceof THREE.Mesh) {
                        mesh = <THREE.Mesh>m;
                        // mesh.receiveShadow = true;
                        // mesh.castShadow = true;

                        if((mesh.name == "Belriv_RDC_2" && !isBelrivProject) || (mesh.name == "Charpente" && isBelrivProject)) {
                            mesh.visible = false;
                        } else {
                            mesh.visible = mesh.name.indexOf(searchString, mesh.name.length - searchString.length) === -1;
                        }

                        console.log(this.TAG, "mesh.name", mesh.name, "- visible", mesh.visible);
                    }
                }
            }
        }
    }

    private _defineBaseModelMaxDimension(obj3d: THREE.Object3D): void {
        const bounds: THREE.Box3 = new THREE.Box3().setFromObject(obj3d);

        const width: number = bounds.max.x - bounds.min.x;
        const height: number = bounds.max.y - bounds.min.y;
        const depth: number = bounds.max.z - bounds.min.z;

        // console.log(this.TAG, width, height, depth);

        this._maxDim = Math.max(width, height, depth);
    }

    /**
     * Get base model's maximal side dimension.
     */
    public getMaxDimension(): number {
        return this._maxDim;
    }

    public getViewWidth(): number {
        return this._viewWidth;
    }

    public getViewHeight(): number {
        return this._viewHeight;
    }

    private parseMaterials(pId: string, materials: { [key: string]: THREE.MeshBasicMaterial; }): void {

        if (this.materials === undefined) {
            this.materials = {};
        }
        for (const key in materials) {
            if (materials.hasOwnProperty(key))
            {
                console.log(this.TAG, "parseMaterials()", pId, key, materials[key]);

                if (this.materials[key] === undefined)
                {
                    this.materials[key] = [];
                }

                this.materials[key].push(materials[key]);

                switch (key)
                {
                    case "goutiere":
                        materials[key].side = THREE.DoubleSide;
                        break;

                    default: break;
                }
            }
        }
    }

    @Input() public set "roof-material"(pValues: string[]) {
        if (!pValues) {
            return;
        }
        this.updateMaterial("roof", pValues);
    }

    @Input() public set "walls-material"(pValues: string[]) {
        if (!pValues) {
            return;
        }
        this.updateMaterial("murs", pValues);
    }

    @Input() public set "belriv-type"(pValues: string[]) {
        if (!pValues) {
            return;
        }
        this.updateMaterial("belriv", pValues);
    }

    @Input() public set "profile-type"(pValues: string[]) {
        if (!pValues) {
            return;
        }
        this.updateMaterial("goutiere", pValues);

        const regex = /assets\/model\/descentes.*/gm;
        const objectDescente = this.pivot.children.find(child => child.name.match(regex) !== null);
        if(objectDescente) {
            this.pivot.remove(objectDescente);
        }

        this._currentSceneObjs['descente'] = "";
    }

    @Input() public set "descente-type"(pValues: string[]) {
        if (!pValues) {
            return;
        }
        this.updateMaterial("descente", pValues);
    }

    @Input() public set "ouvertures-type"(pValues: string[]) {
        if (!pValues) {
            return;
        }
        this.updateMaterial("ouvertures", pValues);
    }

    private _removeObject(pId: string): void
    {
        // console.log(this.TAG, "removeOBJECT", pId, this._objLoadedById, this._objLoadedById[pId]);
        if (this._objLoadedById[pId])
        {
            this.pivot.remove(this._objLoadedById[pId]);
        }
    }

    /**
     *
     * @param pId scene material id
     * @param pValues value 0 => texture url or color string, value 1 => color string (optional)
     */
    public updateMaterial(pId: string, pValues: string[]): void {
        console.log(this.TAG, "updateMaterial()", pId, pValues);

        let value: string;
        let option: string;
        // Main value
        if (pValues && pValues.length > 0) {
            value = pValues[0];
            // Color only
            if (value.indexOf("#") === 0) {
                this._updateColor(pId, value);
                return;
            }

            // Optional value => color
            if (pValues.length > 1) {
                option = pValues[1];
            }
            // Texture image
            if (value.toLowerCase().indexOf(".obj") === -1) {
                this._updateTexture(pId, value);
            }
            // OBJ
            else {
                this._loadObj(pId, value, option);
            }

            // Color option
            if (option && option.indexOf("#") === 0) {
                this._updateColor(pId, option, false);
            }
        }
        else
        {
            this._removeObject(pId);
        }
    }

    private _loadObj(pId: string, pPath: string, pTint?: string): void {
        console.log(this.TAG, "this._loadObj()", pPath);

        if (this._currentSceneObjs[pId] === pPath) {
            return;
        }

        // Remove previous object of the same type
        if (this._objLoaded[this._currentSceneObjs[pId]]) {
            this.pivot.remove(this._objLoaded[this._currentSceneObjs[pId]]);
        }

        // Updates current objects path by
        this._currentSceneObjs[pId] = pPath;

        if (this._objLoaded[pPath]) {
            const searchString = "_1";
            for (const mesh of this._objLoaded[pPath].children) {
                if (mesh instanceof THREE.Mesh) {
                    mesh.visible = mesh.name.indexOf(searchString, mesh.name.length - searchString.length) === -1;
                }
            }

            this.pivot.add(this._objLoaded[pPath]);
            return;
        }

        const pivot: THREE.Object3D = new THREE.Object3D();
        pivot.name = pPath;
        pivot.position.set(0, 0, 0);
        const a: string[] = pPath.split("/");
        a.pop();
        const objPath: string = a.join("/") + "/";
        const fileName: string = pPath.split("/").pop();
        this.loadObj({
            id: pId,
            path: objPath,
            fileObj: fileName,
            texturePath: objPath,
            fileMtl: fileName.replace(".obj", ".mtl"),
            pivot: pivot,
            tint: pTint
        });
    }

    private _updateColor(pMaterialName: string, pColor: string, pDisposeMap: boolean = true): void {
        if (!this.materials || this.materials[pMaterialName] === undefined || pColor.indexOf("#") !== 0) { return; }

        console.log(this.TAG, "_updateColor()", pMaterialName, pColor, pDisposeMap);

        let material: THREE.MeshPhongMaterial;
        for (const m of this.materials[pMaterialName])
        {
            material = (<THREE.MeshPhongMaterial>m);
            if (pDisposeMap && material.map) {
                material.map.dispose();
                material.map = null;
                material.needsUpdate = true;
            }

            material.color = new THREE.Color(pColor);
        }
    }

    private _updateTexture(pMaterialName: string, pPath: string): void {
        if (!this.materials || this.materials[pMaterialName] === undefined) { return; }

        this._updateColor(pMaterialName, "#ffffff");
        this.textureLoader.load(pPath, (texture) => {
            console.log(this.TAG, "_updateTexture()", pMaterialName, pPath);
            let material: THREE.MeshPhongMaterial;
            for (const m of this.materials[pMaterialName])
            {
                material = (<THREE.MeshPhongMaterial>m);
                material.map = texture;
                material.map.wrapS = THREE.RepeatWrapping;
                material.map.wrapT = THREE.RepeatWrapping;

                switch (pMaterialName)
                {
                    // TODO: exception crade. Si le nom des fichiers change, ça ne marche plus.
                    // Exception pour les tuile, ajout d'une normal map pour rattrapper la misère de la texture. Peut mieux faire.
                    case "roof":
                        if (texture.image.src.indexOf("tuiles") !== -1)
                        {
                            // console.log("TEXTURE", texture);
                            // material.shininess = 35;
                            // material.emissive = new THREE.Color("rgb(60,60,60)");
                            // material.bumpMap = this.textureLoader.load("assets/images/toiture/type/tuiles_OCC.png");
                            // material.bumpMap.wrapS = THREE.RepeatWrapping;
                            // material.bumpMap.wrapT = THREE.RepeatWrapping;
                            // material.bumpScale = 0.2;
                            // material.specular = new THREE.Color("rgb(255,113,0)");
                            // material.specularMap = this.textureLoader.load("assets/images/toiture/type/tuiles_SPEC.png");
                            // material.specularMap.wrapS = THREE.RepeatWrapping;
                            // material.specularMap.wrapT = THREE.RepeatWrapping;
                            material.normalMap = this.textureLoader.load("assets/images/toiture/type/tuiles_NRM.jpg");
                            material.normalMap.wrapS = material.normalMap.wrapT = THREE.RepeatWrapping;
                            material.normalScale = new THREE.Vector2(1, 1);
                        } else {
                            material.normalMap = null;
                        }
                        break;

                    // Switch map texture to bump map.
                    case "belriv":
                        material.bumpMap = material.map;
                        //material.map = null;
                        material.shininess = 10;
                        material.specular = new THREE.Color("rgb(255,255,255)");
                        material.bumpMap.wrapS = THREE.RepeatWrapping;
                        material.bumpMap.wrapT = THREE.RepeatWrapping;
                        material.bumpScale = 0.01;
                        break;

                    default: break;
                }

                material.needsUpdate = true;
            }
        });
    }

    public render(): void {
        requestAnimationFrame(this.render.bind(this));
        if (!this.renderer.autoClear) { this.renderer.clear(); }
        this.controls.update();
        // console.log(this.camera.position);
        this.renderer.render(this.scene, this.camera);
    }

    private resetCamera(): void {
        this.camera.position.copy(this.cameraDefaults.posCamera);
        this.cameraTarget.copy(this.cameraDefaults.posCameraTarget);
        this.updateCamera();
    }

    private recalcAspectRatio(): void {
        this.aspectRatio = (this._viewHeight === 0) ? 1 : this._viewWidth / this._viewHeight;
    }

    private updateCamera(): void {
        this.camera.aspect = this.aspectRatio;
        this.camera.lookAt(this.cameraTarget);
        this.camera.updateProjectionMatrix();
    }

    private resizeDisplayGL(): void {
        // this.controls.handleResize();
        if (this.renderer === undefined) { return; }

        this.recalcAspectRatio();
        this.renderer.setSize(this._viewWidth, this._viewHeight, false);
        this.updateCamera();
    }

    private _onResizeEventHandler(e?: Event): void {
        console.log(this.TAG, "_onResizeEventHandler()");

        this._viewWidth = this.canvas.offsetWidth;
        this._viewHeight = this.canvas.offsetHeight;

        this.canvas.width = this._viewWidth;
        this.canvas.height = this._viewHeight;

        this.resizeDisplayGL();
    }

    public addListeners(): void {
        this.removeListeners();

        window.addEventListener("resize", this._onResizeEventHandlerBinded);
    }

    public removeListeners(): void {
        window.removeEventListener("resize", this._onResizeEventHandlerBinded);
    }

    ngAfterViewInit() {
        console.log(this.TAG, "ngAfterViewInit()");

        this.canvas = (<HTMLElement>this.el.nativeElement).querySelector("canvas");

        LoaderUtils.addLoader("threed-loader", <HTMLElement>this.el.nativeElement, true);

        this._onResizeEventHandler();

        if (this.cameraDefaults) {
            this.init();

            this._loadObj("base-model", this["base-model-path"]);
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        // for (let propName in changes) {
        //     let chng = changes[propName];
        //     let cur  = JSON.stringify(chng.currentValue);
        //     let prev = JSON.stringify(chng.previousValue);
        //     console.log(this.TAG, `${propName}: currentValue = ${cur}, previousValue = ${prev}`);
        // }

        if (this.canvas === undefined) { return; }

        if (this.scene === undefined && this.cameraDefaults) {
            this.init();

            this._loadObj("base-model", this["base-model-path"]);
        }
    }

    ngOnDestroy() {
        this.removeListeners();
    }
}



// WEBPACK FOOTER //
// ./src/app/main-view/building-customization/threed-view/threed-view.component.ts